import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  useFreeTier,
  usePatchFreeTierLimit,
  type FreeTierKey,
  type FreeTierLimits,
} from "@/api/admin/free-tier";
import { AdminErrorState } from "@/components/admin/AdminErrorState";

const KEYS: FreeTierKey[] = ["natal", "transits_daily_free", "transit_trial_days"];

const PAGE: CSSProperties = {
  padding: "16px 24px 96px",
  display: "flex",
  flexDirection: "column",
  gap: 16,
  maxWidth: 480,
};
const H1: CSSProperties = { fontFamily: "var(--font-display)", fontSize: 22 };
const NOTE: CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: 13,
  color: "var(--d-fg-muted)",
};
const ROW: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
};
const LABEL: CSSProperties = { fontFamily: "var(--font-body)", fontSize: 14 };
const INPUT: CSSProperties = {
  width: 80,
  padding: "6px 10px",
  borderRadius: 8,
  border: "1px solid var(--d-line)",
  background: "var(--d-ink)",
  color: "var(--d-fg)",
  fontFamily: "var(--font-mono)",
};
const BTN: CSSProperties = {
  padding: "8px 16px",
  borderRadius: 999,
  border: "none",
  background: "var(--d-violet)",
  color: "#fff",
  cursor: "pointer",
};

export function FreeTierPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const { data, isLoading, isError, refetch } = useFreeTier();
  const patch = usePatchFreeTierLimit();
  const [draft, setDraft] = useState<FreeTierLimits | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (data && !draft) setDraft(data);
  }, [data, draft]);

  if (isError) return <AdminErrorState onRetry={() => void refetch()} />;
  if (isLoading || !draft || !data) return <div style={PAGE}>{t("common.loading")}</div>;

  function save() {
    if (!draft || !data) return;
    const changes = KEYS.filter((k) => draft[k] !== data[k]);
    if (changes.length === 0) {
      setStatus(t("free_tier.no_changes"));
      return;
    }
    Promise.all(
      changes.map(
        (k) =>
          new Promise<void>((res, rej) => {
            patch.mutate(
              { feature_key: k, value: draft[k] },
              { onSuccess: () => res(), onError: (e) => rej(e) },
            );
          }),
      ),
    )
      .then(() => setStatus(t("free_tier.updated", { count: changes.length })))
      .catch((e: Error) => setStatus(e.message));
  }

  return (
    <div style={PAGE}>
      <h2 style={H1}>{t("free_tier.title")}</h2>
      <div style={NOTE}>{t("free_tier.note")}</div>
      {KEYS.map((k) => (
        <div key={k} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={ROW}>
            <label htmlFor={k} style={LABEL}>
              {t(`free_tier.limit.${k}`)}
            </label>
            <input
              id={k}
              aria-label={k}
              style={INPUT}
              type="number"
              min={0}
              value={draft[k]}
              onChange={(e) => setDraft({ ...draft, [k]: Number(e.target.value) })}
            />
          </div>
          <div style={NOTE}>{t(`free_tier.hint.${k}`)}</div>
        </div>
      ))}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button style={BTN} onClick={save}>
          {t("common.save")}
        </button>
      </div>
      {status && (
        <div role="status" style={NOTE}>
          {status}
        </div>
      )}
    </div>
  );
}
